import { checkNullOrUndefined, isValidObject } from "app/main/apps/common/AuraFunctions";

/**
 * PGSS-5359 — when the Revision Histories views may show the VAS side.
 *
 * The views used to ask the same question, separately:
 *
 *   !checkNullOrUndefined(order?.vas) && isValidObject(order?.vas) && showVASRevision
 *
 * That reads the order's **live** VAS to decide whether to show its VAS **history**, and those are
 * different questions. They agree right up until the moment someone deletes the VAS — which is exactly
 * when the difference matters. Delete it and every VAS revision the order ever had stops being
 * reachable: `canShowVas` goes false, so the VAS segment disappears and `visibleRounds` narrows to
 * product types. Nothing was deleted — the rows are still in `order_detail_history`, which is why the
 * next revision still numbers itself REV0003 and looks to a tester like the history "reset" underneath it.
 *
 * Revision history records what happened. Deleting the VAS is one more thing that happened; it does not
 * un-happen the rounds that came before it.
 *
 * So the rule is split in two. `showVASRevision` stays exactly as it was — that is the caller's
 * deliberate choice about what this instance is for (SubmitVas and ChooseSupplier force a VAS-only or
 * product-only list, and customers get their own scope). What changes is the second half: an order
 * qualifies if it has VAS **now** or had VAS **rounds** before.
 */

/** Round `revision_type` values that belong to the product side of the split. */
export const PRODUCT_REVISION_TYPES = new Set(["quote", "order", "drawing"]);

/** Round `revision_type` values that belong to the VAS side of the split. */
export const VAS_REVISION_TYPES = new Set(["proof", "vas_price", "vas_final"]);

/** Does the order carry a VAS design right now? The original test, unchanged. */
export function hasLiveVas(order) {
  return !checkNullOrUndefined(order?.vas) && isValidObject(order?.vas);
}

/**
 * Has this order ever had a VAS revision round?
 *
 * Answered from whichever feed the view actually loaded (rounds preferred, legacy as fallback):
 *
 *   rounds              GET /api/orders/{id}/revisions — one mixed list where each round states its
 *                       own `revision_type`.
 *   legacyVasRevisions  GET /api/getRevisionHistory — already split server-side, so membership is the
 *                       fact that the entry is in this list at all (see LIST_SCOPE_VAS in
 *                       RevisionHistories).
 *
 * A round whose type predates PGSS-5041 carries no `revision_type` and is deliberately not counted:
 * it cannot be classified as VAS safely, and the product branch already shows those.
 */
export function hasVasRevisionHistory({ rounds, legacyVasRevisions } = {}) {
  if (Array.isArray(rounds) && rounds.some((round) => VAS_REVISION_TYPES.has(round?.revision_type))) {
    return true;
  }

  return Array.isArray(legacyVasRevisions) && legacyVasRevisions.length > 0;
}

/**
 * The whole rule: may this view show the VAS side?
 *
 * `showVASRevision` first and on its own — a caller that has switched the VAS side off means it,
 * whatever the order looks like.
 */
export function canShowVasRevisions({
  order,
  showVASRevision = true,
  rounds,
  legacyVasRevisions,
} = {}) {
  if (!showVASRevision) {
    return false;
  }

  return hasLiveVas(order) || hasVasRevisionHistory({ rounds, legacyVasRevisions });
}
